import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

// A text the bot shows on screen, quoted exactly.
function Msg({ children, red }) {
  return (
    <span className={`font-mono text-xs ${red ? 'text-red-400' : 'text-gray-300'}`}>{children}</span>
  );
}

function PageAutoSell({ navigate }) {
  return (
    <div>
      <h1 className="text-[2.5rem] leading-[1.1] font-semibold text-white tracking-[-0.02em] mb-3">Auto Sell</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Auto Sell empties your bag of the things you do not want to keep. You do not have to stand
        at a merchant &mdash; the sale is sent directly, from wherever you are. Everything is on the{' '}
        <strong className="text-white">FARM &rsaquo; Sell</strong> tab.
      </p>

      <Callout type="danger">
        <strong>Selling cannot be undone.</strong> This is the one feature that can genuinely cost
        you something, so read this page before you turn off Dry run. A sold pet or mount cannot be
        bought back.
      </Callout>

      <DocHeading>The Safe Way to Start</DocHeading>
      <Step n={1} title="Leave Dry run on">
        <strong>Dry run</strong> sits under <strong>ADVANCED</strong> on the{' '}
        <strong>AUTO SELL</strong> card, and it is on by default. While it is on, the bot makes every
        decision and sends nothing. The card reminds you with an orange line:{' '}
        <em>Dry run is on (Advanced) - nothing is actually sold.</em>
      </Step>
      <Step n={2} title="Lock what must never go">
        On{' '}
        <button onClick={() => navigate('bag')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">FARM &rsaquo; Bag</button>,
        lock the slots that hold your keepers. The bot does not even look into a locked slot, and
        it sells nothing until the locks of the character you are playing are loaded.
      </Step>
      <Step n={3} title="Check the filter">
        In <strong>WHAT TO SELL</strong>, open <strong>Categories</strong> and look at every tick,
        then at the rarity and grade lists under the ticked ones. Do not rely on the defaults &mdash;
        make the list yours.
      </Step>
      <Step n={4} title="Press PREVIEW">
        If anything matches, a pop-up says <Msg>Dry run: nothing was sold - see the list window.</Msg>{' '}
        and the list window opens by itself, showing every unlocked slot marked <strong>SELL</strong>{' '}
        or <strong>keep</strong>. Read the SELL rows, item by item, at least once.
      </Step>
      <Step n={5} title="Only then turn Dry run off">
        The button changes from PREVIEW to <strong>SELL NOW</strong>. If you also want it to sell by
        itself while you farm, turn on the switch in the <strong>AUTO SELL</strong> title row and
        pick a trigger in <strong>WHEN TO SELL</strong>.
      </Step>

      <Shot
        name="auto-sell-01-sell-tab.png"
        tab="FARM › Sell"
        caption="Auto Sell after a round: 214 items sold this session; the green line says the locks of this character are loaded and the bag is settled."
        shoot="menushot (stage sell + sellgame, sx_bag): a FARM › Sell lap Dry run KI-vel — AUTO SELL BE, Sell key F7, SELL NOW / WHAT WOULD GO, Status Done, In the plan 14, Sold 214, Earned, a zöld Ready sor; jobbra WHAT TO SELL (2 kategória, Gear unidentified Common/Uncommon/Magic) és WHEN TO SELL (Sell when the bag runs out BE, Free slots below 5)."
      />

      <DocHeading>The AUTO SELL Card</DocHeading>
      <DocTable
        headers={['Control', 'What it does']}
        rows={[
          [<strong className="text-white">Switch in the title row</strong>, 'Lets it sell on its own, using the WHEN TO SELL card. Off means it only sells when you press the button or the key. While it is on, the FARM tab shows a green dot.'],
          [<strong className="text-white">Sell key</strong>, <>A key that starts a sale right away (a preview while Dry run is on). Press it again to stop a running sale. It only reacts while the game window is in front, and it works <em>even while the switch is off</em>.</>],
          [<strong className="text-white">PREVIEW / SELL NOW</strong>, 'Runs it once, right now. The label tells you which of the two it will be.'],
          [<strong className="text-white">WHAT WOULD GO / HIDE THE LIST</strong>, 'Opens or closes the list window described below.'],
          [<strong className="text-white">STOP</strong>, 'Appears while a sale is running. What has not been sent yet stays in your bag.'],
          [<><strong className="text-white">ADVANCED</strong> &rsaquo; <strong className="text-white">Dry run</strong></>, 'The safety brake: decides everything, sends nothing.'],
          [<><strong className="text-white">ADVANCED</strong> &rsaquo; <strong className="text-white">Per command</strong></>, 'How many items go out in one command (1–5, default 5).'],
          [<><strong className="text-white">ADVANCED</strong> &rsaquo; <strong className="text-white">Delay</strong></>, 'The pause between commands (50–1000 ms, default 100). The defaults are fine; slow it down only if the game stutters while selling.'],
        ]}
      />
      <Callout type="warning">
        <strong>Pick a key you will not hit by accident.</strong> If you bind Caps Lock, Shift, Ctrl,
        Alt, Space, Enter, Tab, Esc, Backspace, a Windows key, Num Lock or Scroll Lock, the card
        warns you in red. Every key press also shows a pop-up &mdash;{' '}
        <Msg>Hotkey: preview only (dry run).</Msg> or <Msg>Hotkey: SELLING now.</Msg> &mdash; so a
        sale never starts silently.
      </Callout>

      <DocHeading>Status Lines</DocHeading>
      <DocTable
        headers={['Line', 'What it means']}
        rows={[
          [<strong className="text-white">Status</strong>, <>What it is doing right now, for example <Msg>Off</Msg>, <Msg>Reading the bag...</Msg>, <Msg>Selling...</Msg>, <Msg>Checking what is left...</Msg>, <Msg>Done</Msg> or <Msg>Dry run</Msg>.</>],
          [<strong className="text-white">In the plan</strong>, <>How many items the current round will sell. If it reads like <Msg>X of Y</Msg> in orange, more items matched than fit in one round &mdash; the rest follow automatically.</>],
          [<strong className="text-white">Sold</strong>, 'How many items were sent this session.'],
          [<strong className="text-white">Earned</strong>, 'The gold this session’s sales brought in, in gold, silver and copper. It is measured from your wallet before and after each sale, so anything else that moves your gold during a sale (a pickup, a repair) is counted too.'],
          [<strong className="text-white">Progress</strong>, 'Shown while a round is running: how far it is.'],
        ]}
      />
      <DocHeading>The Safety Line</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Under the numbers, one line says whether a sale may go out right now. It is checked again
        right before every batch is sent, so a sale that started cannot outrun it.
      </p>
      <DocTable
        headers={['Line', 'What it means']}
        rows={[
          [<span className="text-emerald-400">Ready: the bag locks of NAME are loaded, the bag is settled.</span>, 'Everything is in place. NAME is the character you are playing.'],
          [<span className="text-amber-300">Waiting: your character is not in the world yet.</span>, 'Loading screen or character select.'],
          [<span className="text-amber-300">Waiting: the bag locks of NAME are not loaded yet.</span>, 'Right after logging in or switching characters. It clears by itself in a moment.'],
          [<span className="text-amber-300">Waiting: saving your bag locks...</span>, 'You just changed a lock. The sale waits until it is safely on the disk, so a restart cannot lose it.'],
          [<span className="text-amber-300">Waiting: checking that your bag has finished loading (1 of 2).</span>, 'After entering a map the game rebuilds your bag. The bot waits until it has read the bag twice, a moment apart, with nothing moving.'],
          [<span className="text-red-400">Stopped: the bag lock file of NAME could not be loaded - see the Bag tab.</span>, <>The file that holds this character&rsquo;s locks is damaged. Nothing is sold or melted until you decide on {' '}<button onClick={() => navigate('bag')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">FARM &rsaquo; Bag</button>.</>],
          [<span className="text-red-400">Stopped: your bag locks could not be saved - see the Bag tab.</span>, 'A lock that is not on the disk would not survive a restart, so selling stops until saving works again. It keeps retrying.'],
          [<span className="text-red-400">Stopped: too many items remembered from locked slots - see the Bag tab.</span>, 'See “The lock follows the item” below. Unlock slots you no longer need.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        While the line is not green, a sale waits (30 seconds when you started it, 15 when it started
        by itself), then gives up with <Msg>Not sold - …</Msg> and the reason. Nothing is ever
        sold on a guess.
      </p>

      <p className="text-sm text-gray-400 mb-4">
        <strong className="text-white">One press finishes the job.</strong> After each round the bot
        reads the bag again and carries on while matching items are left, for up to 8 rounds. It stops
        when nothing matches any more, or when a round made no progress &mdash; then the status reads{' '}
        <Msg>Some items were not accepted</Msg> and those items stay in your bag.
      </p>

      <DocHeading>When to Sell</DocHeading>
      <DocTable
        headers={['Setting', 'What it does']}
        rows={[
          [<strong className="text-white">Sell instantly after pickup</strong>, 'Shortly after the bot picks something up, it sells whatever in the bag matches your filter. It checks at most every 3 seconds and shows no pop-up for these small sales, so your bag simply stays empty while you farm.'],
          [<strong className="text-white">Sell when the bag runs out</strong>, 'Starts a sale when your free bag slots drop to the number below or fewer.'],
          [<strong className="text-white">Free slots below</strong>, 'The trigger for the switch above (0–20, default 3).'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        As the card itself says: nothing is ever sold from a locked slot, and nothing is sold until
        your locks are loaded and the bag has finished loading after you enter a map.
      </p>
      <Callout type="warning">
        <strong>&ldquo;It never sells by itself.&rdquo;</strong> Both triggers need the switch in the{' '}
        <strong>AUTO SELL</strong> title row <strong>on</strong> and <strong>Dry run off</strong>. With
        Dry run on they do nothing at all. Also, &ldquo;after pickup&rdquo; means pickups made by the
        bot (<button onClick={() => navigate('auto-loot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Auto Loot</button>), not items you pick up by hand.
      </Callout>

      <DocHeading>What to Sell</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The <strong className="text-white">Categories</strong> list on the{' '}
        <strong className="text-white">WHAT TO SELL</strong> card holds everything the bot may sell.
        The closed list shows your choice (<em>All</em>, <em>None</em>, the names, or &ldquo;N
        selected&rdquo;). Some categories get a second list of their own, under the same name, once you
        tick them:
      </p>
      <DocTable
        headers={['Category', 'Extra list once ticked']}
        rows={[
          [<strong className="text-white">Gear, unidentified</strong>, 'Rarity: Common, Uncommon, Magic, Rare, Epic, Unique, Set, Mythic.'],
          [<strong className="text-white">Gear, identified</strong>, 'Its own rarity list, separate from the unidentified one.'],
          [<strong className="text-white">Gems</strong>, 'Grade: a (Flawed) to i (Royal), then the Trapezoid, Imperial and Square grades.'],
          [<strong className="text-white">Runes</strong>, 'Grade: 0 (Cracked) to 4 (Flawless).'],
          [<strong className="text-white">Essences</strong>, 'Grade: Common, Uncommon, Magic, Rare, Epic.'],
          [<><strong className="text-white">Jewels</strong>, <strong className="text-white">Costumes</strong>, <strong className="text-white">Materials</strong></>, 'No extra list: the tick sells all of them.'],
          [<><strong className="text-white">Pets</strong>, <strong className="text-white">Mounts</strong></>, <>No extra list. The card shows the reminder <Msg>A sold pet or mount cannot be bought back.</Msg></>],
          [<strong className="text-white">Everything else</strong>, 'See the warning below: on the sell side this tick does not sell anything.'],
        ]}
      />
      <Callout type="warning">
        <strong>&ldquo;Not sure&rdquo; always means keep.</strong> Anything the bot cannot put into a
        named category is never sold, even with <em>Everything else</em> ticked. The same goes for an
        item whose rarity, gem grade, rune grade or essence grade cannot be read: it stays in the
        bag.
      </Callout>
      <Callout type="danger">
        <strong>A stack is always sold whole.</strong> The sale cannot take &ldquo;3 out of 40
        000&rdquo;. If a category is ticked, every stack of it in an unlocked slot goes completely.
        Lock the slot of any stack you want to keep. In the list window, quantities above 1 are
        shown in yellow for this reason.
      </Callout>

      <DocHeading>Single Items: the Item List</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The <strong className="text-white">EDIT ITEM LIST</strong> button opens{' '}
        <button onClick={() => navigate('item-lists')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">FARM &rsaquo; Item lists</button>{' '}
        on its <strong className="text-white">SELL FILTER</strong> side, where you can name single
        items. The <strong className="text-white">Item rules</strong> line under the button counts
        how many sell rules you have.
      </p>
      <DocTable
        headers={['Rule', 'Effect on selling']}
        rows={[
          [<strong className="text-white">KEEP</strong>, 'Never sold, whatever the categories say. This always wins.'],
          [<strong className="text-white">SELL</strong>, 'Sold even if its category is not ticked. It cannot open up gold, keys, quest or travel items, or items the bot cannot classify.'],
          ['No rule', 'The categories decide.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        A rule never beats a locked slot or any of the safety checks below.
      </p>

      <DocHeading>The WHAT WOULD GO Window</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        This is where you check the bot before you trust it. The window stays open with the menu
        closed and refreshes itself about once a second &mdash; even while Auto Sell is switched
        off. Its rows come from the same decision the sale itself uses, so what it says is exactly
        what would happen.
      </p>
      <DocTable
        headers={['Part', 'What it shows']}
        rows={[
          ['Top line', <><Msg>DRY RUN is ON - this is a preview, nothing is sold.</Msg> in green, or <Msg red>DRY RUN is OFF - these items WILL be sold for real.</Msg> in red.</>],
          ['Summary', 'How many items would go, how many unlocked slots were looked at, and how old the reading is.'],
          [<><strong className="text-white">Refresh now</strong> / <strong className="text-white">Close</strong></>, 'Reads the bag again right away / closes the window.'],
          [<strong className="text-white">Show keep items too</strong>, 'Also lists the items that stay, with the reason. Off: only the SELL rows are shown.'],
          ['Columns', 'Slot, Item (hover for details), Qty, Action (SELL or keep) and Reason.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">A few reasons you will see in the list:</p>
      <DocTable
        headers={['Reason', 'What it means']}
        rows={[
          [<Msg>category off</Msg>, 'Its category is not ticked.'],
          [<Msg>rarity off</Msg>, <>The category is ticked, but not this rarity (similarly <Msg>gem tier off</Msg>, <Msg>rune tier off</Msg>, <Msg>essence tier off</Msg>).</>],
          [<Msg>item list: never</Msg>, 'You marked it KEEP in the item list.'],
          [<Msg>item list: always</Msg>, 'You marked it SELL in the item list.'],
          [<><Msg>not classified - never sold</Msg>, <Msg>stack of an unclassified item</Msg></>, 'The bot cannot tell what it is, so it keeps it.'],
          [<Msg>rarity unknown - kept</Msg>, <>The rarity could not be read, so it stays (similarly <Msg>gem tier unknown - kept</Msg>, <Msg>rune tier unknown - kept</Msg>, <Msg>essence tier unknown - kept</Msg>).</>],
          [<><Msg>protected</Msg>, <Msg>protected category</Msg></>, 'Gold, keys, lockpicks, quest or travel item — never sold.'],
          [<Msg>was in a locked slot this run</Msg>, 'It sat in a locked slot when this sale started, so it stays protected until the sale ends.'],
        ]}
      />
      <p className="text-sm text-gray-400 mb-4">
        For a SELL row the reason names the category that let it through, for example{' '}
        <Msg>gear_unid</Msg>. Locked slots never appear in the list at all.
      </p>

      <DocHeading>What Is Never Sold</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        None of these has a tick box or a setting that could open it up &mdash; a box you could
        click by accident is exactly the risk being removed:
      </p>
      <DocTable
        headers={['Never sold', 'Why']}
        rows={[
          ['Anything in a locked slot', 'Your explicit decision. The bot does not read locked slots at all, and no filter or item rule can override it.'],
          ['An item that came out of a locked slot', 'See below: the lock follows the item while its old slot stays locked.'],
          ['Gear with gems in its sockets', 'The gems are often worth more than the item and would go with it. The same when the bot cannot read the sockets. Gems in a stack of their own are not affected.'],
          ['Gold and keys', 'They do not even appear in the sell categories. Keys and lockpicks are included.'],
          ['Quest and travel items', 'Same: no tick exists for them on the sell side. Losing one can break a quest.'],
          ['Items outside the bag', 'What you wear and what sits outside your bag tabs is never touched.'],
          ['Anything it cannot read or classify', 'An unreadable amount, an unreadable grade, an unknown kind of item, or a slot it cannot confirm: all mean keep.'],
        ]}
      />
      <Callout type="info">
        <strong>The lock follows the item.</strong> You lock a slot, but the bot also remembers
        which item was sitting in it. If that item moves &mdash; the game&rsquo;s sort button, a
        drag, a rearrange &mdash; it stays protected wherever it lands, for as long as its old slot
        is locked. On the Bag grid such an item is drawn in orange. Unlocking the old slot releases
        it.
      </Callout>

      <DocHeading>Checks Right Before Sending</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        The plan is made first and sent in small batches. Things can change in between, so each
        batch is checked once more, on a fresh reading of the bag:
      </p>
      <DocTable
        headers={['If…', 'What happens']}
        rows={[
          ['you locked its slot, turned its category off, or the item is gone', 'It stays — the fresh decision wins.'],
          ['a stack grew since the plan was made', 'It is skipped this round and planned again in the next one.'],
          ['the bot cannot confirm which slot the item is in', <>It is kept, and a pop-up says <Msg>Could not confirm where some items sit - they were kept, not sold.</Msg></>],
          ['your route ends during a sale', <>The sale stops: <Msg>Route ended - sale stopped</Msg>.</>],
          ['you change map or instance during a sale', <>The sale stops: <Msg>Map changed - sale stopped</Msg>. Start it again after you arrive.</>],
        ]}
      />

      <DocHeading>When It Does Not Sell</DocHeading>
      <DocTable
        headers={['You see', 'What it means']}
        rows={[
          [<Msg>Off</Msg>, 'The switch in the AUTO SELL title row is off. The buttons and the key still work.'],
          [<Msg>Dry run</Msg>, 'Dry run is on (under ADVANCED). Nothing is sent.'],
          [<Msg>Nothing matches the filter</Msg>, 'Open WHAT WOULD GO, turn on Show keep items too, and read the reasons.'],
          [<Msg red>Bag data looks wrong - selling is blocked.</Msg>, <>The bag reading does not make sense this moment, so the bot refuses to sell (status <Msg>Stopped: bag data looks wrong</Msg>, pop-up <Msg>The bag reads back inconsistently - nothing was sold.</Msg>). It clears by itself.</>],
          [<Msg>Could not read the inventory by slot - nothing sold.</Msg>, 'A pop-up: the bot could not tell which item sits in which slot, so it sold nothing. Try again in a moment.'],
          [<Msg red>The bag read was dropped - this is the previous list.</Msg>, 'The last reading was skipped; what you see is the one before. Usually gone on the next reading.'],
          [<Msg red>Item list truncated - some items are not visible to the bot.</Msg>, 'You carry more items than the bot can read at once. What it cannot see, it does not sell — sell in two passes.'],
          [<Msg>Waiting for the game thread</Msg>, <>The game is not processing the command. The pop-up says <Msg>The game thread is not running our command - move your character.</Msg> &mdash; do that.</>],
          [<Msg>Finished - some items were not accepted.</Msg>, 'The server refused some items. They stay in your bag.'],
          [<Msg>Queue full - retrying</Msg>, 'Momentary; it retries by itself.'],
          [<Msg red>Too many locked slots to guard safely - nothing sold.</Msg>, 'More locked items than one sale can keep track of. It sells nothing rather than protect only some — unlock slots you no longer need.'],
          [<Msg red>Stopped: the sell command does not look like it used to. Probably a game patch.</Msg>, <>A game update changed something, and the feature locked itself (status <Msg>Disabled: unexpected command layout</Msg>). Wait for a bot update.</>],
        ]}
      />

      <Callout type="info">
        Not everything has to be sold. Runes, gear and gems can be melted instead, and cubes opened
        &mdash; see{' '}
        <button onClick={() => navigate('auto-melt')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Melt &amp; Cubes</button>.
        The category list looks like the one in{' '}
        <button onClick={() => navigate('auto-loot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">Auto Loot</button>,
        but the two settings are separate. Your sell filter can be saved in a{' '}
        <button onClick={() => navigate('farm-profiles')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">farm profile</button>;
        loading a profile never turns the AUTO SELL switch on, never touches Dry run and never changes
        your key. The WHEN TO SELL switches are saved with the profile, but they do nothing until you
        turn the AUTO SELL switch on yourself.
      </Callout>
    </div>
  );
}

export default PageAutoSell;
